import React, {useEffect, useMemo, useState} from 'react';
import { motion, useAnimation } from 'framer-motion';
import CoverLetter from "../components/CoverLetter";
import api from "../api/api";
import { CheckSquare,
         Circle,
         CheckCircle,
         Package,
         PackageOpen,
         RotateCcw,
         Loader,
         Mail,
         ExternalLink,
         Sparkles,
       } from 'lucide-react';

export const applicationsData = [
  {
    id: 1,
    title: "Senior Frontend Developer",
    company: "TechCorp Industries",
    location: "Remote",
    applicationDate: "2026-01-28",
    postinglink: "www.google.com",
    coverLetter: "www.techcorp/applications/coverletter/12345",
    referral: true,
    referralContact: "john.smith@email.com",
    referralContactName: "John Smith",
    status: "Pursuing",
    tasks: [
      {
        taskID: 1,
        title: "Ask for a Referral",
      }
    ]
  },
  {
    id: 2,
    title: "Product Designer",
    company: "Design Studio Labs",
    location: "New York, NY",
    applicationDate: "2026-01-25",
    postinglink: "www.google.com",
    coverLetter: "",
    referral: false,
    referralContact: "",
    referralContactName: "",
    status: "Not Pursuing",
    tasks: [
      {
        taskID: 2,
        title: "Ask for a Referral"
      }
    ]
  },
  {
    id: 3,
    title: "Marketing Manager",
    company: "Growth Partners Inc",
    location: "Austin, TX",
    applicationDate: "2026-01-22",
    postinglink: "www.google.com",
    coverLetter: "www.growthpartners.com/applications/coverletter/67890",
    referral: false,
    referralContact: "sarah.johnson@email.com",
    referralContactName: "Sarah Johnson",
    status: "Pursuing",
    tasks: [
      {
        taskID: 3,
        title: "Ask for a Referral"
      }, {
        taskID: 4,
        title: "Write a Cover Letter"
      }
    ]
  },
  {
    id: 4,
    title: "Data Analyst",
    company: "Analytics Pro",
    location: "San Francisco, CA",
    applicationDate: "2026-01-20",
    postinglink: "www.google.com",
    coverLetter: "",
    referral: true,
    referralContact: "mike.chen@email.com",
    referralContactName: "Mike Chen",
    status: "Pursuing",
    tasks: []
  }
];

function SwipeToArchive({ isDarkMode, onArchive, actionLabel, actionIcon, children }) {
  const controls = useAnimation();
  const REVEAL = 80;
  const handleDragEnd = (event, info) => {
    controls.start({ x: info.offset.x < -REVEAL / 2 ? -REVEAL : 0 });
  };
  return (
    <div className="md:hidden relative overflow-hidden">
      <button
        type="button"
        onClick={onArchive}
        className="absolute inset-y-0 right-0 flex w-20 flex-col items-center justify-center gap-1 bg-red-600 text-white text-xs font-semibold"
      >
        {actionIcon}
        {actionLabel}
      </button>
      <motion.div
        drag="x"
        dragConstraints={{ left: -REVEAL, right: 0 }}
        dragElastic={0.08}
        animate={controls}
        onDragEnd={handleDragEnd}
        className={`relative ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}
      >
        {children}
      </motion.div>
    </div>
  );
}

export default function Applications({
  isDarkMode,
  searchTerm = '',
  statusFilter = 'all',
  referralFilter = 'all',
  coverLetterFilter = 'all',
  initialApplications = null,
  isFocusView
}) {
  const APPLICATIONS_STORAGE_KEY = 'applicationsTrackerData';
  const [showCoverLetter, setShowCoverLetter] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [applicationsState, setApplicationsState] = useState(() => {
    if (Array.isArray(initialApplications)) {
      return initialApplications;
    }
    try {
      const savedApplications = localStorage.getItem(APPLICATIONS_STORAGE_KEY);
      if (!savedApplications) return applicationsData;
      const parsedApplications = JSON.parse(savedApplications);
      return Array.isArray(parsedApplications) ? parsedApplications : applicationsData;
    } catch {
      return applicationsData;
    }
  });
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [showReferralConfirmModal, setShowReferralConfirmModal] = useState(false);
  const [selectedReferralJobId, setSelectedReferralJobId] = useState(null);
  const [selectedCoverLetterJobId, setSelectedCoverLetterJobId] = useState(null);
  const [referralFriendName, setReferralFriendName] = useState('');
  const [referralFriendEmail, setReferralFriendEmail] = useState('');
  const [, setActiveTasks] = useState([]);
  const [applyLoading, setApplyLoading] = useState(false);
  const [selectedJobID, setSelectedJobID] = useState(null);
  const [hoveredJobID, setHoveredJobID] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(APPLICATIONS_STORAGE_KEY, JSON.stringify(applicationsState));
    } catch {
      // ignore write failures
    }
  }, [APPLICATIONS_STORAGE_KEY, applicationsState]);

  useEffect(() => {
    if (!Array.isArray(initialApplications)) return;

    setApplicationsState((prev) => {
      const prevById = new Map(prev.map((item) => [String(item.id), item]));

      return initialApplications.map((item) => {
        const existing = prevById.get(String(item.id));
        if (!existing) return item;

        return {
          ...item,
          coverLetter: existing.coverLetter ?? item.coverLetter,
          referral: typeof existing.referral === 'boolean' ? existing.referral : item.referral,
          referralContact: existing.referralContact ?? item.referralContact,
          referralContactName: existing.referralContactName ?? item.referralContactName,
          status: item.status ?? existing.status,
          tasks: Array.isArray(existing.tasks) && existing.tasks.length > 0 ? existing.tasks : item.tasks,
        };
      });
    });
  }, [initialApplications]);

  useEffect(() => {
    let isMounted = true;

    const fetchActiveTasks = async () => {
      try {
        const response = await api.get('/task/activetasks');
        if (!isMounted) return;
        setActiveTasks(Array.isArray(response.data) ? response.data : []);
      } catch {
        if (!isMounted) return;
        setActiveTasks([]);
      }
    };

    fetchActiveTasks();
    return () => {
      isMounted = false;
    };
  }, []);

  // Handle opening a cover letter
  const handleCoverLetter = (event, job) => {
    if (event) {
      event.stopPropagation();
    }
    setSelectedCoverLetterJobId(job?.id || null);
    setShowCoverLetter(true);
  };

  const applications = useMemo(() => {
    let result = applicationsState;

    const searchLower = searchTerm.trim().toLowerCase();
    if (searchLower) {
      result = result.filter((job) =>
        String(job.id || '').toLowerCase().includes(searchLower) ||
        job.title?.toLowerCase().includes(searchLower) ||
        job.company?.toLowerCase().includes(searchLower) ||
        job.location?.toLowerCase().includes(searchLower) ||
        job.status?.toLowerCase().includes(searchLower)
      );
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'applied') {
        result = result.filter((job) => job.status === 'Applied');
      }
      if (statusFilter === 'pursuing') {
        result = result.filter((job) => job.status === 'Pursuing');
      }
      if (statusFilter === 'interviewing') {
        result = result.filter((job) => job.status === 'Interviewing');
      }
      if (statusFilter === 'offer') {
        result = result.filter((job) => job.status === 'Offer');
      }
      if (statusFilter === 'rejected') {
        result = result.filter((job) => job.status === 'Rejected');
      }
      if (statusFilter === 'not-pursuing') {
        result = result.filter((job) => job.status === 'Not Pursuing');
      }
    }

    if (referralFilter !== 'all') {
      if (referralFilter === 'Completed' || referralFilter === 'pursuing') {
        result = result.filter((job) => job.referral === true);
      }
      if (referralFilter === 'Not Completed' || referralFilter === 'not-pursuing') {
        result = result.filter((job) => job.referral === false);
      }
    }

    if (coverLetterFilter !== 'all') {
      if (coverLetterFilter === 'Completed') {
        result = result.filter((job) => Boolean(job.coverLetter));
      }
      if (coverLetterFilter === 'Not Completed') {
        result = result.filter((job) => !job.coverLetter);
      }
    }

    return result;
  }, [applicationsState, searchTerm, statusFilter, referralFilter, coverLetterFilter]);

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateString;
    }
  };

  const normalizeUrl = (url) => {
    if (!url) return '#';
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  };

  const generateReferralLink = (job, friendName, friendEmail) => {
    if (!friendEmail) return '#';
    
    const subject = `Please Refer Me: ${job.title} at ${job.company}`;
    const greetingName = friendName || 'there';
    
    const body = `Hi ${greetingName},\n\n` +
      `I'm interested in the following position at your company and would greatly appreciate if you could refer me:\n\n` +
      `Position: ${job.title}\n` +
      `Company: ${job.company}\n` +
      `Location: ${job.location}\n` +
      `Posted: ${formatDate(job.applicationDate)}\n` +
      `Job Link: ${normalizeUrl(job.postinglink)}\n\n` +
      `I believe my skills and experience would be a great fit for this role. Your referral would mean a lot to me!\n\n` +
      `Thank you,\nYour Friend`;

    return `mailto:${friendEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleReferralClick = (event, job) => {
    event.stopPropagation();
    setSelectedReferralJobId(job.id);
    setReferralFriendName(job.referralContactName || '');
    setReferralFriendEmail(job.referralContact || '');
    setShowReferralModal(true);
  };

  const handleOpenReferralEmail = async () => {
    const selectedJob = applicationsState.find((job) => job.id === selectedReferralJobId);
    if (!selectedJob || !referralFriendEmail.trim()) return;

    const mailtoLink = generateReferralLink(
      selectedJob,
      referralFriendName.trim(),
      referralFriendEmail.trim()
    );

    setApplicationsState((prev) =>
      prev.map((job) =>
        job.id === selectedReferralJobId
          ? {
              ...job,
              referralContactName: referralFriendName.trim(),
              referralContact: referralFriendEmail.trim()
            }
          : job
      )
    );

    try {
      await api.post(`/jobs/tracker/${selectedReferralJobId}/referral`, {
        referralName: referralFriendName.trim(),
        referralEmail: referralFriendEmail.trim()
      });
    } catch (err) {
      console.error('Failed to save referral:', err);
    }

    window.location.href = mailtoLink;
    setShowReferralModal(false);
    setShowReferralConfirmModal(true);
  };

  const markReferralAsCompleted = async () => {
    setApplicationsState((prev) =>
      prev.map((job) =>
        job.id === selectedReferralJobId
          ? { ...job, referral: true }
          : job
      )
    );

    try {
      await api.post(`/jobs/tracker/${selectedReferralJobId}/referral`, {
        referralName: referralFriendName.trim(),
        referralEmail: referralFriendEmail.trim()
      });
    } catch (err) {
      console.error('Failed to mark referral complete:', err);
    }

    setShowReferralConfirmModal(false);
    setSelectedReferralJobId(null);
  };

  const handleCoverLetterSaved = async (payload) => {
    if (!selectedCoverLetterJobId) {
      setShowCoverLetter(false);
      return;
    }

    // Let errors propagate to CoverLetter's onSave caller so the modal stays
    // open and shows the failure instead of silently closing as if it saved.
    await api.post(`/jobs/tracker/${selectedCoverLetterJobId}/cover-letter`, payload || {});

    setApplicationsState((prev) =>
      prev.map((job) =>
        job.id === selectedCoverLetterJobId
          ? { ...job, coverLetter: true }
          : job
      )
    );

    setShowCoverLetter(false);
    setSelectedCoverLetterJobId(null);
  };

  const closeReferralConfirmModal = () => {
    setShowReferralConfirmModal(false);
    setSelectedReferralJobId(null);
  };

  const handleStatusChange = async (jobId, nextStatus) => {
    setApplicationsState((prev) =>
      prev.map((job) =>
        job.id === jobId
          ? { ...job, status: nextStatus }
          : job
      )
    );

    try {
      await api.patch(`/jobs/tracker/${jobId}/status`, { status: nextStatus });
    } catch (err) {
      console.error('Failed to update job tracker status:', err);
    }
  };

  // Handle Applying Job for "I Applied !" button
  const handleApplied = async (job) => {
      if (!job?.unique_job_id) {
          return;
      }
      setSelectedJobID(job.id)
      setApplyLoading(true);

      try {
          const response = await api.post(`/jobs/${job.unique_job_id}/apply`);
          
          console.log('Status:', response.status)
          console.log(response.data)

      } catch (error) {
          console.error('Error recording application:', error);
      } finally {
          setApplyLoading(false);
          setSelectedJobID(null);
      }
      setApplicationsState((prev) =>
        prev.map((j) =>
          j.id === job.id
            ? { ...j, applicationDate: new Date().toISOString() }
            : j
        )
      );
  };

  // this handle function put the selected job record (in Job Tracker) to archive (set isActive = 0 in database)
  const handleArchive = async (jobID, activeStatus) => {
    if (!jobID) {
        console.log("Need job ID to update active status")
        return;
    }
    setArchiveLoading(true);

    try {
        const response = await api.patch(`/jobs/tracker/${jobID}/archive`, {active: activeStatus});
        
        console.log('Status:', response.status)
        console.log(response.data)

    } catch (error) {
        console.error('Error moving application to archive:', error);
    } finally {
        setArchiveLoading(false);
        setSelectedJobID(null);
    }
    setApplicationsState((prev) =>
      prev.filter((j) => j.id !== jobID)
    );
  }

  const ArchiveBtn = ({job}) => {
    const [archiveHover, setArchiveHover] = useState(false);
    return (
      <>
        {selectedJobID === job.id && (
          archiveLoading ? <Loader size={20} className='animate-spin'/>
            : (isFocusView ?
              <button
                title='Put to Archive'
                onMouseEnter={() => setArchiveHover(true)}
                onMouseLeave={() => setArchiveHover(false)}
                onClick={() => handleArchive(job.id, 0)}
              >
                {archiveHover
                  ? <PackageOpen size={20}/>
                  : <Package size={20}/>
                }
              </button> :
              <button
                title='Back to Focus'
                onClick={() => handleArchive(job.id, 1)}
                className={`hover:text-primary-600`}
              >
                <RotateCcw size={20}/>
              </button>
        ))}
      </>
    )
  }

  return (
    <div>
      <div className={`${isDarkMode ? 'divide-y divide-slate-700' : 'divide-y divide-slate-200'}`}>
        {applications.length === 0 && (
          <div className={`rounded-2xl border p-8 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600'}`}>
            No tracked applications found yet. Apply to a job in Job Browser and click "Yes, I Applied".
          </div>
        )}
        {applications.map(job => {
          return (
            <div
              key={job.id}
              className={`md:px-6 md:py-3 transition-colors ${
                isDarkMode ? 'bg-slate-800/40 hover:bg-slate-900' : 'bg-white hover:bg-slate-50'
              }`}
              onMouseEnter={() => setHoveredJobID(job.id)} 
              onMouseLeave={() => setHoveredJobID(null)}
            >
                  {/* Mobile card (swipe left to reveal Archive) */}
                  <SwipeToArchive
                    isDarkMode={isDarkMode}
                    onArchive={() => handleArchive(job.id, isFocusView ? 0 : 1)}
                    actionLabel={isFocusView ? 'Archive' : 'Restore'}
                    actionIcon={isFocusView ? <Package size={18} /> : <RotateCcw size={18} />}
                  >
                    <div className="px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className={`text-sm font-semibold min-w-0 ${isDarkMode ? 'text-slate-100' : 'text-slate-900'}`}>
                        {job.title}
                      </h3>
                      <div className="flex items-center gap-2 shrink-0">
                        <a
                          href={job.postinglink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`inline-flex items-center ${isDarkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'}`}
                        >
                          <ExternalLink size={20} />
                        </a>
                        <ArchiveBtn job={job} />
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3" onClick={(event) => event.stopPropagation()}>
                      <div className="min-w-0 space-y-1">
                        <div className={`text-sm font-medium truncate ${isDarkMode ? 'text-slate-200' : 'text-slate-700'}`}>{job.company}</div>
                        <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{job.location}</div>
                        {job.applicationDate
                          ? <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Applied {formatDate(job.applicationDate)}</div>
                          : <span className={`inline-flex justify-center text-xs rounded-full border-4 px-2 py-1 font-semibold opacity-60 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-300' : 'bg-white border-slate-300 text-slate-500'}`}>Not Applied Yet</span>}
                      </div>
                      <div className="flex flex-col items-start gap-2">
                        <button
                          type="button"
                          onClick={() => setShowComingSoon(true)}
                          className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-dashed ${isDarkMode ? 'border-slate-600 text-slate-400 hover:bg-slate-700/50' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}
                        >
                          <Sparkles size={14} className={isDarkMode ? 'text-violet-400' : 'text-violet-500'} />
                          Cover Letter
                        </button>
                        {job.referral === true ? (
                          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-lg ${isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'}`}>
                            Referral
                            <CheckSquare size={16} className="text-green-500" />
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 text-xs font-semibold bg-blue-600 text-white p-2 px-3 rounded-lg hover:bg-blue-500"
                            onClick={(event) => handleReferralClick(event, job)}
                          >
                            Referral
                            <Mail size={16} className="text-white" />
                          </button>
                        )}
                        {job.applicationDate ? (
                          <select
                            value={job.status || 'Applied'}
                            onChange={(event) => handleStatusChange(job.id, event.target.value)}
                            className={`w-full text-sm rounded-lg border px-2 py-1 ${isDarkMode ? 'bg-slate-800 border-slate-600 text-slate-200' : 'bg-white border-slate-300 text-slate-700'}`}
                          >
                            <option value="Applied">Applied</option>
                            <option value="Pursuing">Pursuing</option>
                            <option value="Interviewing">Interviewing</option>
                            <option value="Offer">Offer</option>
                            <option value="Rejected">Rejected</option>
                            <option value="Not Pursuing">Not Pursuing</option>
                          </select>
                        ) : (
                          <button
                            title='Update to Applied'
                            className="inline-flex items-center justify-center gap-2 text-xs font-semibold text-gray-600 p-2 px-3 rounded-lg bg-gray-300 hover:bg-gray-200"
                            onClick={() => handleApplied(job)}
                          >
                            {(selectedJobID === job.id && applyLoading) ? "Recording..." : "I Applied !"}
                          </button>
                        )}
                      </div>
                    </div>
                    </div>
                  </SwipeToArchive>

                  {/* Desktop grid row */}
                  <div className="hidden md:grid grid-cols-[4fr_2fr_2fr_2fr_2fr_2fr_2fr_1fr] gap-4 items-center">
                  <div
                    className="flex items-center py-1 rounded-lg hover:border-gray-400 gap-3"
                  >
                    <button 
                      onClick={() => {
                        setSelectedJobID(prevJobID => prevJobID === job.id ? null : job.id)
                      }}
                    >
                      {selectedJobID === job.id
                        ? <CheckCircle size={17} className="text-primary-500"/>
                        : <Circle 
                            size={17} 
                            className={`
                              ${isDarkMode ? "text-slate-700" : "text-slate-200"} 
                              ${hoveredJobID === job.id ? "opacity-100" : "opacity-0"}
                            `}
                          />
                      }
                    </button>
                    <div className="inline-flex text-md text-left gap-2 py-2 rounded-lg align-left">
                      {job.title}
                    </div>
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {job.company}
                  </div>
                  <div className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {job.location}
                  </div>
                  {job.applicationDate 
                    ? <div className={`pl-2 text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        {formatDate(job.applicationDate)}
                      </div>
                    : <div className={`inline-flex items-center`}>
                        <span className={`justify-center text-xs rounded-full border-4 px-2 py-1 font-semibold opacity-60 ${
                      isDarkMode
                        ? 'bg-slate-800 border-slate-600 text-slate-300'
                        : 'bg-white border-slate-300 text-slate-500'
                      }`}>Not Applied Yet</span>
                      </div>
                  }
                  <div className={isDarkMode ? 'text-slate-400' : 'text-slate-600'} onClick={(event) => event.stopPropagation()}>
                    <span className="inline-flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setShowComingSoon(true)}
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-dashed ${isDarkMode ? 'border-slate-600 text-slate-400 hover:bg-slate-700/50' : 'border-slate-300 text-slate-500 hover:bg-slate-50'}`}
                      >
                        <Sparkles size={14} className={isDarkMode ? 'text-violet-400' : 'text-violet-500'} />
                        Cover Letter
                      </button>
                    </span>
                  </div>
                  <div className={isDarkMode ? 'text-slate-400' : 'text-slate-600'} onClick={(event) => event.stopPropagation()}>
                    <span className="inline-flex items-center gap-1">
                      {job.referral === true ? (
                        <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-2 px-3 rounded-lg ${
                          isDarkMode ? 'bg-slate-700 text-slate-200' : 'bg-slate-100 text-slate-700'
                        }`}>
                          Referral
                          <CheckSquare size={16} className="text-green-500" />
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="inline-flex items-center gap-2 text-xs font-semibold bg-blue-600 text-white p-2 px-3 rounded-lg hover:bg-blue-500"
                          onClick={(event) => handleReferralClick(event, job)}
                        >
                          Referral
                          <Mail size={16} className="text-white" />
                        </button>
                      )}
                    </span>
                  </div>
                  {job.applicationDate
                    ? <div onClick={(event) => event.stopPropagation()}>
                        <select
                          value={job.status || 'Applied'}
                          onChange={(event) => handleStatusChange(job.id, event.target.value)}
                          className={`text-sm rounded-lg border px-2 py-1 ${
                            isDarkMode
                              ? 'bg-slate-800 border-slate-600 text-slate-200'
                              : 'bg-white border-slate-300 text-slate-700'
                          }`}
                        >
                          <option value="Applied">Applied</option>
                          <option value="Pursuing">Pursuing</option>
                          <option value="Interviewing">Interviewing</option>
                          <option value="Offer">Offer</option>
                          <option value="Rejected">Rejected</option>
                          <option value="Not Pursuing">Not Pursuing</option>
                        </select>
                      </div>
                    : <span className="inline-flex items-center gap-1">
                        <button title='Update to Applied' className={`inline-flex items-center justify-center gap-2 text-xs font-semibold text-gray-600 p-2 px-3 rounded-lg bg-gray-300 hover:bg-gray-200`}
                          onClick={() => handleApplied(job)}>
                          {(selectedJobID === job.id && applyLoading) ? "Recording..." : "I Applied !"}
                        </button>
                      </span>
                  }
                  <div className={`flex items-center justify-between ${isDarkMode ? 'text-slate-300' : 'text-slate-500'}`}>
                    <a
                      href={job.postinglink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-1 ${isDarkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-700'}`}
                    >
                      <ExternalLink size={20}/>
                    </a>
                    <ArchiveBtn job={job}/>
                  </div>
                  </div>
            </div>
          );
        })}
      </div>
      <CoverLetter
        isOpen={showCoverLetter}
        jobTrackerId={selectedCoverLetterJobId}
        onClose={() => {
          setShowCoverLetter(false);
          setSelectedCoverLetterJobId(null);
        }}
        onSave={handleCoverLetterSaved}
      />
      {showComingSoon && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowComingSoon(false)}
        >
          <div
            className={`w-full max-w-md rounded-2xl border p-6 text-center ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'}`}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={`mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full ${isDarkMode ? 'bg-violet-900/40' : 'bg-violet-100'}`}>
              <Sparkles size={28} className={isDarkMode ? 'text-violet-300' : 'text-violet-600'} />
            </div>
            <div className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full mb-3 ${isDarkMode ? 'bg-violet-900/40 text-violet-300' : 'bg-violet-100 text-violet-700'}`}>
              <Sparkles size={12} />
              New AI Feature
            </div>
            <h3 className="text-lg font-bold mb-2">AI Cover Letters — Coming Soon</h3>
            <p className={`text-sm mb-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              We're building an AI-powered cover letter generator to help you tailor and apply faster. Stay tuned!
            </p>
            <button
              type="button"
              onClick={() => setShowComingSoon(false)}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white hover:bg-violet-500"
            >
              Got it
            </button>
          </div>
        </div>
      )}
      {showReferralModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div
            className={`w-full max-w-md rounded-2xl border p-6 ${
              isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-4">Ask a Friend for Referral</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold mb-1">Friend Name</label>
                <input
                  type="text"
                  value={referralFriendName}
                  onChange={(event) => setReferralFriendName(event.target.value)}
                  placeholder="Enter friend name"
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${
                    isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-300 text-slate-900'
                  }`}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1">Friend Email</label>
                <input
                  type="email"
                  value={referralFriendEmail}
                  onChange={(event) => setReferralFriendEmail(event.target.value)}
                  placeholder="friend@email.com"
                  className={`w-full rounded-lg border px-3 py-2 text-sm ${
                    isDarkMode ? 'bg-slate-900 border-slate-700 text-slate-100' : 'bg-white border-slate-300 text-slate-900'
                  }`}
                />
              </div>
            </div>
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                className={`px-3 py-2 text-xs font-semibold rounded-lg ${
                  isDarkMode ? 'bg-slate-700 text-slate-100 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                onClick={() => {
                  setShowReferralModal(false);
                  setSelectedReferralJobId(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!referralFriendEmail.trim()}
                className="px-3 py-2 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={handleOpenReferralEmail}
              >
                Open Email
              </button>
            </div>
          </div>
        </div>
      )}
      {showReferralConfirmModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div
            className={`w-full max-w-md rounded-2xl border p-6 ${
              isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-100' : 'bg-white border-slate-200 text-slate-900'
            }`}
            onClick={(event) => event.stopPropagation()}
          >
            <h3 className="text-lg font-bold mb-2">Referral Sent?</h3>
            <p className={`text-sm mb-5 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
              Did you actually send the referral email request?
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                className={`px-3 py-2 text-xs font-semibold rounded-lg ${
                  isDarkMode ? 'bg-slate-700 text-slate-100 hover:bg-slate-600' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                onClick={closeReferralConfirmModal}
              >
                No
              </button>
              <button
                type="button"
                className="px-3 py-2 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-500"
                onClick={markReferralAsCompleted}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}